import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Pause, Play, RotateCcw, Sparkles } from 'lucide-react';
import ShowcaseStage from './ShowcaseStage.jsx';
import { INTRO_REEL, clampFrame, getIntroBeatIndex } from './showcaseTimeline.js';
import { useInView, useMediaQuery, useReducedMotion, useShowcaseRuntime } from './useShowcaseRuntime.js';
import { formatShowcaseCopy } from './showcaseCopyFormat.js';

// Pinned scroll-scrubbing is a desktop treatment. Narrower screens play the
// reel while it is on screen; reduced motion shows chapter stills on demand.
const PIN_QUERY = '(min-width: 1100px) and (prefers-reduced-motion: no-preference)';
const DURATION = INTRO_REEL.durationInFrames;
const LAST_FRAME = DURATION - 1;
const BEATS = INTRO_REEL.beats;

const HomeShowcaseIntro = ({
  copy,
  language = 'en',
  localeTag = 'en-US',
  theme = 'true-dark',
  studioHref,
  openStudioLabel,
  onScrollToStudio,
  renderActionElement
}) => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const stickyRef = useRef(null);
  const stageWrapRef = useRef(null);
  const segmentsRef = useRef(null);
  const playerRef = useRef(null);
  const frameRef = useRef(0);
  const lastSeekRef = useRef(-1);
  const activeBeatRef = useRef(0);
  const manualPlayRef = useRef(false);
  const userPausedRef = useRef(false);
  const endedRef = useRef(false);

  const runtime = useShowcaseRuntime(sectionRef, { language });
  const reducedMotion = useReducedMotion();
  const pinned = useMediaQuery(PIN_QUERY);
  const nearViewport = useInView(sectionRef, { threshold: 0, rootMargin: '240px 0px' });
  const stageInView = useInView(stageWrapRef, { threshold: 0.55 });
  const mode = reducedMotion ? 'reduced' : pinned ? 'scrub' : 'autoplay';

  const [live, setLive] = useState(false);
  const [activeBeat, setActiveBeat] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [atStart, setAtStart] = useState(true);

  const beats = Array.isArray(copy?.beats) ? copy.beats : [];
  const inputProps = useMemo(() => ({ copy, language, localeTag, theme }), [copy, language, localeTag, theme]);

  const seek = useCallback((frame) => {
    const next = clampFrame(frame, DURATION);
    if (next === lastSeekRef.current) return;
    lastSeekRef.current = next;
    playerRef.current?.seekTo(next);
  }, []);

  const handleFrame = useCallback((frame) => {
    frameRef.current = frame;
    lastSeekRef.current = frame;
    if (endedRef.current && frame < LAST_FRAME - 1) {
      endedRef.current = false;
      setEnded(false);
    }
    const segments = segmentsRef.current?.children;
    if (segments) {
      BEATS.forEach((beat, index) => {
        const progress = Math.min(1, Math.max(0, (frame - beat.from) / (beat.to - beat.from)));
        segments[index]?.style.setProperty('--stk-segment', progress.toFixed(3));
      });
    }
    const index = getIntroBeatIndex(frame);
    if (index !== activeBeatRef.current) {
      activeBeatRef.current = index;
      setActiveBeat(index);
    }
    setAtStart(frame < 8);
  }, []);

  const handlePlayingChange = useCallback((isPlaying) => {
    setPlaying(isPlaying);
    if (isPlaying) {
      endedRef.current = false;
      setEnded(false);
    }
    if (!isPlaying) manualPlayRef.current = false;
  }, []);

  const handleEnded = useCallback(() => {
    manualPlayRef.current = false;
    endedRef.current = true;
    setEnded(true);
  }, []);

  const handleLive = useCallback(() => setLive(true), []);

  // Scroll geometry in viewport px (zoom-safe). The reel is pinned while the
  // track's top runs from the sticky offset down by `range`.
  const getScrollRange = useCallback(() => {
    const track = trackRef.current;
    const sticky = stickyRef.current;
    if (!track || !sticky) return null;
    const trackRect = track.getBoundingClientRect();
    const stickyRect = sticky.getBoundingClientRect();
    const range = trackRect.height - stickyRect.height;
    if (range <= 0) return null;
    const zoom = track.offsetHeight ? trackRect.height / track.offsetHeight : 1;
    const stickyTop = (parseFloat(window.getComputedStyle(sticky).top) || 0) * zoom;
    return { trackRect, range, stickyTop };
  }, []);

  // Desktop: the frame follows the scroll position through the pinned track,
  // eased toward its target so wheel steps read as smooth motion.
  useEffect(() => {
    if (mode !== 'scrub' || !live || !nearViewport) return undefined;
    let rafId = 0;
    let running = false;
    let current = frameRef.current;

    const targetFrame = () => {
      const metrics = getScrollRange();
      if (!metrics) return current;
      const progress = Math.min(1, Math.max(0, (metrics.stickyTop - metrics.trackRect.top) / metrics.range));
      return progress * LAST_FRAME;
    };

    const tick = () => {
      if (manualPlayRef.current) {
        running = false;
        return;
      }
      const target = targetFrame();
      const diff = target - current;
      current = Math.abs(diff) < 0.4 ? target : current + diff * 0.2;
      seek(current);
      if (Math.abs(target - current) >= 0.4) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };

    const kick = () => {
      if (manualPlayRef.current) {
        manualPlayRef.current = false;
        playerRef.current?.pause();
        current = frameRef.current;
      }
      if (running) return;
      running = true;
      rafId = window.requestAnimationFrame(tick);
    };

    // Playback left over from autoplay (e.g. the window widened past the pin
    // breakpoint) would fight the scroll position.
    if (!manualPlayRef.current && playerRef.current?.isPlaying()) playerRef.current.pause();
    current = targetFrame();
    seek(current);
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
    };
  }, [getScrollRange, live, mode, nearViewport, seek]);

  // Narrow screens: play while the reel is on screen, pause when it leaves.
  useEffect(() => {
    if (mode !== 'autoplay' || !live) return;
    const player = playerRef.current;
    if (!player) return;
    if (stageInView && !userPausedRef.current && !ended) {
      player.play();
    } else if (player.isPlaying()) {
      player.pause();
    }
  }, [ended, live, mode, stageInView]);

  // Reduced motion: hold the active chapter's finished state.
  useEffect(() => {
    if (mode !== 'reduced' || !live) return;
    playerRef.current?.pause();
    seek(BEATS[activeBeatRef.current].hold);
  }, [live, mode, seek]);

  const goToBeat = (index) => {
    const beat = BEATS[index];
    if (!beat) return;
    if (mode === 'scrub') {
      const metrics = getScrollRange();
      if (!metrics) return;
      manualPlayRef.current = false;
      playerRef.current?.pause();
      const top = window.scrollY + metrics.trackRect.top - metrics.stickyTop + (beat.hold / LAST_FRAME) * metrics.range;
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }
    activeBeatRef.current = index;
    setActiveBeat(index);
    if (mode === 'reduced') {
      playerRef.current?.pause();
      seek(beat.hold);
      return;
    }
    userPausedRef.current = false;
    setEnded(false);
    seek(beat.from);
    playerRef.current?.play();
  };

  const togglePlayback = () => {
    const player = playerRef.current;
    if (!player) return;
    if (player.isPlaying()) {
      player.pause();
      userPausedRef.current = true;
      manualPlayRef.current = false;
      return;
    }
    if (frameRef.current >= LAST_FRAME - 1) seek(0);
    userPausedRef.current = false;
    manualPlayRef.current = mode === 'scrub';
    setEnded(false);
    player.play();
  };

  const PlaybackIcon = playing ? Pause : ended ? RotateCcw : Play;
  const playbackLabel = playing ? copy.pauseLabel : ended ? copy.replayLabel : copy.playLabel;
  const showScrollHint = mode === 'scrub' && atStart && !playing;

  if (!copy || !beats.length) return null;

  return (
    <section
      id="guest-showcase-reel"
      ref={sectionRef}
      className={`stk-showcase stk-intro stk-mode-${mode}${live ? ' is-live' : ''}`}
      aria-labelledby="guest-showcase-reel-title"
      lang={language}
    >
      <div className="stk-intro-track" ref={trackRef}>
        <div className="stk-intro-sticky" ref={stickyRef}>
          <div className="premium-shell stk-intro-grid">
            <div className="stk-intro-copy">
              <p className="premium-eyebrow">{copy.eyebrow}</p>
              <h2 id="guest-showcase-reel-title" className="stk-heading">{copy.title}</h2>
              <p className="premium-section-description stk-intro-description">{copy.description}</p>

              <ol className="stk-chapters" aria-label={copy.chaptersLabel}>
                {beats.map((beat, index) => {
                  const isActive = index === activeBeat;
                  return (
                    <li key={beat.id} className={`stk-chapter${isActive ? ' is-active' : ''}${index < activeBeat ? ' is-done' : ''}`}>
                      <button
                        type="button"
                        className="stk-chapter-button"
                        onClick={() => goToBeat(index)}
                        aria-current={isActive ? 'step' : undefined}
                        aria-label={`${formatShowcaseCopy(copy.chapterLabel, { index: index + 1, count: beats.length })}: ${beat.title}`}
                        aria-describedby={`stk-beat-${beat.id}-desc`}
                      >
                        <span className="stk-chapter-index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                        <span className="stk-chapter-text">
                          <strong>{beat.title}</strong>
                          <span className="stk-chapter-detail" id={`stk-beat-${beat.id}-desc`}>{beat.description}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>

              <div className="stk-intro-actions">
                <a className="premium-button premium-button--primary" href="#guest-live-trial">
                  <Sparkles size={17} aria-hidden="true" />
                  {copy.tryLiveDemo}
                </a>
                {renderActionElement && studioHref ? renderActionElement({
                  href: studioHref,
                  onClick: onScrollToStudio,
                  className: 'premium-button premium-button--dark-ghost',
                  children: <>{openStudioLabel}<ArrowRight size={16} aria-hidden="true" /></>
                }) : null}
              </div>
            </div>

            <div className="stk-intro-stage-column">
              <div className="stk-intro-stage-wrap" ref={stageWrapRef}>
                <div className="stk-segments" ref={segmentsRef} aria-hidden="true">
                  {BEATS.map((beat) => <span key={beat.id} className="stk-segment" />)}
                </div>
                <ShowcaseStage
                  ref={playerRef}
                  runtime={runtime}
                  compositionId="intro"
                  inputProps={inputProps}
                  onFrame={handleFrame}
                  onPlayingChange={handlePlayingChange}
                  onEnded={handleEnded}
                  onLive={handleLive}
                  variant="reel"
                />
                {mode === 'autoplay' ? (
                  // Touch shortcut for the labelled Play/Pause button below.
                  <button type="button" className="stk-stage-tap" onClick={togglePlayback} tabIndex={-1} aria-hidden="true" />
                ) : null}
              </div>
              <div className="stk-intro-controls">
                <button
                  type="button"
                  className="stk-icon-button"
                  onClick={togglePlayback}
                  disabled={!live}
                  aria-label={playbackLabel}
                >
                  <PlaybackIcon size={18} aria-hidden="true" />
                  {/* All labels share one grid cell, so the button keeps the
                      widest label's width and never shifts the column. */}
                  <span className="stk-label-swap" aria-hidden="true">
                    {[copy.playLabel, copy.pauseLabel, copy.replayLabel].map((label, index) => (
                      <span key={index} className={label === playbackLabel ? 'is-current' : undefined}>{label}</span>
                    ))}
                  </span>
                </button>
                {mode === 'scrub' ? (
                  <span className={`stk-scroll-hint${showScrollHint ? '' : ' is-hidden'}`} aria-hidden={showScrollHint ? undefined : 'true'}>
                    {copy.scrollHint}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeShowcaseIntro;
